import Box from '@mui/material/Box'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import TitleContext from '../../../TitleContext'
import { events as eventApi } from '../../../api/swagger'
import EventSummaryTable from '../EventSummaryTable'
import TodaysEventCard from '../../Card/TodaysEventCard'
import SearchBar from '../../Search/SearchBar'
import DropDown, { DropDownVariant } from '../../Menus/DropDown'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import {
  EventEventTypeEnum,
  FetchEventsResponse,
  OrderByDirection,
  PaginationResponse,
} from '../../../swagger'
import { useLoadingIds } from '../../../hooks/useLoadingIds'
import { EventSortBy } from '../EventEnums'
import getOrderByClause from '../../../utils/getOrderByClause'
import useLoadingContext from '../../../hooks/useLoadingContext'
import { useAppSnackbar } from '../../../hooks/useAppSnackbar'
import { useMountEffect } from '../../../hooks/useMountEffect'
import { DEFAULT_PAGE_SIZE } from '../../../utils/constants'
import { createSpacetime, getTodaysEarliestEvent } from '../Events'
import spacetime from 'spacetime'
import { dateToDashString } from '../../../utils/dateUtility'
import { reinterpretYearMonthDayAsLocalTime } from '../../../utils/reinterpretYearMonthDayAsLocalTime'
import Card from '@mui/material/Card'
import CardFormHeader from '../../Card/CardFormHeader'
import Header, { HeaderVariant } from '../../Elements/Header'
import Tooltip from '@mui/material/Tooltip'
import Help from '@mui/icons-material/Help'
import DateField, { DateFieldVariant } from '../../Elements/DateField'
import { Page } from '../../Elements/PageMargins'
import EventCardMobile from '../../Card/EventCardMobile'
import OutlinedButton, {
  OutlinedButtonVariant,
} from '../../Buttons/OutlinedButton'
import LoadingProgress from '../../Elements/LoadingProgress'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/system'
import React, {
  useContext,
  useMemo,
  useState,
  useCallback,
  ChangeEvent,
} from 'react'

const internalEventTypes = [
  EventEventTypeEnum.SalesOrientation,
  EventEventTypeEnum.Retreats,
  EventEventTypeEnum.LcdOrientation,
  EventEventTypeEnum.Practicum,
  EventEventTypeEnum.OtherLeadershipTraining,
]

interface LeadershipTrainingRequest {
  page: number
  pageSize: number
  orderBy: Array<string>
  search?: string
  eventType?: string
  fromDate?: string
}

const defaultSortProperty = EventSortBy.StartDate
const defaultSortOrder = OrderByDirection.Asc

const MobileSectionHeader = styled(Header)(({ theme }) => ({
  margin: theme.spacing(3, 'auto'),
  padding: 0,
}))

const LeadershipTrainingEvents: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { useTitleEffect } = useContext(TitleContext)

  useTitleEffect(t('LeadershipTrainingEvents.Title', 'Events'))

  const { handleError } = useAppSnackbar()

  const { LeadershipTrainingEvents: leadershipLoadingIds } = useLoadingIds()

  const fetchLeadershipTrainingLoadingId =
    leadershipLoadingIds.fetchLeadershipTraining

  const theme = useTheme()
  const defaultFromDate = useMemo(() => dateToDashString(new Date()), [])
  const isMobileOrSmaller = useMediaQuery(theme.breakpoints.down('sm'))

  const [searchTerm, setSearchTerm] = useState('')
  const [selectedEventType, setSelectedEventType] = useState('')
  const [fromDate, setFromDate] = useState(defaultFromDate)
  const [minDateFilter, setMinDateFilter] = useState(defaultFromDate)
  const [events, setEvents] = useState<FetchEventsResponse[]>([])
  const [pagination, setPagination] = useState<PaginationResponse>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalCount: 0,
    orderBy: [{ [defaultSortProperty]: defaultSortOrder }],
  })

  const [requestParams, setRequestParams] = useState<LeadershipTrainingRequest>(
    {
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      orderBy: [`${defaultSortProperty} ${defaultSortOrder}`],
      search: undefined,
      eventType: undefined,
      fromDate: defaultFromDate,
    }
  )

  useMountEffect(() => {
    const today = new Date()
    const earliestAllowedDate = new Date()
    earliestAllowedDate.setDate(1)
    earliestAllowedDate.setMonth(0)
    earliestAllowedDate.setFullYear(today.getFullYear() - 2)

    const localizedMinDate =
      reinterpretYearMonthDayAsLocalTime(earliestAllowedDate)

    setMinDateFilter(dateToDashString(localizedMinDate))
  })

  const fetchEvents = useCallback(async () => {
    try {
      const { events: fetchedEvents, pagination: nextPagination } =
        await eventApi.fetchLeadershipTraining({
          page: requestParams.page,
          pageSize: requestParams.pageSize,
          orderBy: requestParams.orderBy,
          search: requestParams.search,
          eventType: requestParams.eventType,
          fromDate: requestParams.fromDate,
        })

      setEvents(fetchedEvents ?? [])

      if (nextPagination) {
        setPagination((prev) => ({
          page: nextPagination.page ?? requestParams.page,
          pageSize: nextPagination.pageSize ?? requestParams.pageSize,
          totalCount: nextPagination.totalCount ?? prev.totalCount,
          orderBy: nextPagination.orderBy ?? prev.orderBy,
        }))
      }
    } catch (error) {
      await handleError(
        error,
        t(
          'LeadershipTrainingEvents.Fetch.Error',
          'An unknown error occurred fetching leadership training events.'
        )
      )
    }
  }, [handleError, requestParams, t])

  const { triggerFetch: triggerFetchEvents, isLoading } = useLoadingContext({
    loadingId: fetchLeadershipTrainingLoadingId,
    asyncFunction: fetchEvents,
  })

  const isInitialLoading = isLoading && events.length === 0

  useMountEffect(triggerFetchEvents)

  const handlePaginationChange = useCallback(
    (pageInformation: PaginationResponse) => {
      const nextPage = pageInformation.page + 1
      const nextPageSize = pageInformation.pageSize

      setPagination((prev) => ({
        page: nextPage,
        pageSize: nextPageSize,
        totalCount: prev.totalCount,
        orderBy: pageInformation.orderBy ?? prev.orderBy,
      }))

      setRequestParams((prev) => ({
        ...prev,
        page: nextPage,
        pageSize: nextPageSize,
        orderBy: getOrderByClause(pageInformation.orderBy) ?? prev.orderBy,
      }))

      triggerFetchEvents()
    },
    [triggerFetchEvents]
  )

  const handleViewMoreClick = useCallback(() => {
    handlePaginationChange({
      ...pagination,
      page: 0,
      pageSize: pagination.pageSize + DEFAULT_PAGE_SIZE,
    })
  }, [handlePaginationChange, pagination])

  const handleEventClick = useCallback(
    (eventRow: FetchEventsResponse) => {
      navigate(
        `/events/event-details/${eventRow.eventKey}/leadership-training/overview`,
        {
          state: {
            eventName: eventRow.name,
          },
        }
      )
    },
    [navigate]
  )

  const todaysEvent = useMemo(() => getTodaysEarliestEvent(events), [events])

  const isNextEventToday = useMemo(() => {
    if (!todaysEvent) return false
    return spacetime.now().isSame(createSpacetime(todaysEvent), 'day')
  }, [todaysEvent])

  const todaysEventHeaderLabel = isNextEventToday
    ? t('LeadershipTrainingEvents.Card.Header.Today', "Today's Event")
    : t('LeadershipTrainingEvents.Card.Header.Upcoming', 'Upcoming Event')

  const handleSearch = useCallback(
    (searchText: string) => {
      const trimmed = searchText.trim()
      const searchValue = trimmed.length > 0 ? trimmed : undefined

      setSearchTerm(searchText)
      setRequestParams((prev) => ({
        ...prev,
        page: 1,
        search: searchValue,
      }))
      setPagination((prev) => ({ ...prev, page: 1 }))
      triggerFetchEvents()
    },
    [triggerFetchEvents]
  )

  const handleEventTypeSelection = useCallback(
    (_selection: string, _id: string, selectOptionId: string | number) => {
      const nextEventType = String(selectOptionId)
      setSelectedEventType(nextEventType)

      setRequestParams((prev) => ({
        ...prev,
        page: 1,
        eventType: nextEventType ? nextEventType : undefined,
        search: undefined,
      }))
      setPagination((prev) => ({
        ...prev,
        page: 1,
      }))
      triggerFetchEvents()
    },
    [triggerFetchEvents]
  )

  const handleDateChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const nextDate = event.target.value
      setFromDate(nextDate)

      setRequestParams((prev) => ({
        ...prev,
        page: 1,
        fromDate: nextDate || undefined,
        search: undefined,
      }))

      setPagination((prev) => ({
        ...prev,
        page: 1,
      }))
      triggerFetchEvents()
    },
    [triggerFetchEvents]
  )

  const handleDateKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      event.preventDefault()
    },
    []
  )

  const filterTooltipText = t(
    'LeadershipTrainingEvents.AddTittleToolTip.TittleTranslation',
    'Search for events by date, past or future, to see all events on that date and after'
  )

  const eventTypeOptions = useMemo(() => {
    const labelForEventType = (eventType: string) => {
      switch (eventType) {
        case EventEventTypeEnum.InformationMeeting:
          return t(
            'LeadershipTrainingEvents.EventTypes.InformationMeeting',
            'Information Meeting'
          )
        case EventEventTypeEnum.OpenHouse:
          return t(
            'LeadershipTrainingEvents.EventTypes.OpenHouse',
            'Open House'
          )
        case EventEventTypeEnum.Practicum:
          return t('LeadershipTrainingEvents.EventTypes.Practicum', 'Practicum')
        default:
          return eventType
      }
    }

    const typedOptions = Object.values(internalEventTypes).map((eventType) => ({
      id: eventType,
      name: labelForEventType(eventType),
    }))

    const optionsWithFallback =
      typedOptions.length > 0
        ? typedOptions
        : selectedEventType
        ? [
            {
              id: selectedEventType,
              name: labelForEventType(selectedEventType),
            },
          ]
        : []

    return [
      {
        name: t(
          'LeadershipTrainingEvents.EventTypes.AllEventTypes',
          'All Event Types'
        ),
        id: '',
      },
      ...optionsWithFallback,
    ]
  }, [selectedEventType, t])

  const hasMoreEvents = useMemo(
    () => pagination.totalCount > events.length,
    [events.length, pagination.totalCount]
  )

  const filterControls = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 2, md: 3 },
        alignItems: { xs: 'stretch', md: 'center' },
        width: '100%',
      }}
    >
      <SearchBar
        handleSearch={handleSearch}
        searchValue={searchTerm}
        placeholder={t(
          'LeadershipTraining.Search.Placeholder',
          'Search leadership training'
        )}
      />
      <Box sx={{ width: { xs: '100%', md: 220 } }}>
        <DropDown
          id="leadership-event-type"
          menuOptions={eventTypeOptions}
          handleSelection={handleEventTypeSelection}
          variant={DropDownVariant.SortAndFilter}
          defaultValue={t(
            'LeadershipTrainingEvents.FilterOption.EventTypes',
            'All Event Types'
          )}
          value={selectedEventType}
          formControlProps={{
            width: '100%',
          }}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: { xs: '100%', md: theme.spacing(35) },
          gap: 1,
        }}
      >
        <DateField
          dateVariant={DateFieldVariant.EventsByDate}
          inputProps={{
            min: minDateFilter,
          }}
          variant="filled"
          type="date"
          fullWidth
          onChangeForParent={handleDateChange}
          value={fromDate}
          onKeyDown={handleDateKeyDown}
          hideLabel
          sx={{
            '& input': {
              height: '100%',
              padding: theme.spacing(1.3),
            },
          }}
          InputProps={{
            disableUnderline: true,
            sx: {
              height: theme.spacing(4.5),
            },
          }}
        />
        <Tooltip title={filterTooltipText}>
          <Help color="action" />
        </Tooltip>
      </Box>
    </Box>
  )

  const desktopFilterControls = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 3,
      }}
    >
      <Box
        sx={{
          marginRight: theme.spacing(3),
        }}
      >
        <SearchBar
          handleSearch={handleSearch}
          searchValue={searchTerm}
          placeholder={t(
            'LeadershipTrainingEvents.Search.Placeholder',
            'Search leadership training'
          )}
          sx={{
            '& .MuiOutlinedInput-root': {
              margin: 0,
            },
          }}
        />
      </Box>
      <Box
        sx={{
          width: 220,
          marginRight: theme.spacing(3),
        }}
      >
        <DropDown
          id="leadership-event-type-desktop"
          menuOptions={eventTypeOptions}
          handleSelection={handleEventTypeSelection}
          variant={DropDownVariant.SortAndFilter}
          defaultValue={t(
            'LeadershipTrainingEvents.FilterOption.EventTypes',
            'All Event Types'
          )}
          value={selectedEventType}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <DateField
          dateVariant={DateFieldVariant.EventsByDate}
          inputProps={{
            min: minDateFilter,
          }}
          variant="filled"
          type="date"
          fullWidth
          onChangeForParent={handleDateChange}
          value={fromDate}
          onKeyDown={handleDateKeyDown}
          hideLabel
          sx={{
            '& input': {
              height: '100%',
              padding: theme.spacing(1.3),
            },
          }}
          InputProps={{
            disableUnderline: true,
            sx: {
              height: theme.spacing(4.5),
            },
          }}
        />
        <Tooltip title={filterTooltipText}>
          <Help color="action" />
        </Tooltip>
      </Box>
    </Box>
  )

  return (
    <Page withinTab>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {isMobileOrSmaller ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {todaysEvent && (
              <>
                <MobileSectionHeader
                  id="leadership-training-todays-event-header-mobile"
                  headerName={todaysEventHeaderLabel}
                  component="h2"
                />
                <EventCardMobile
                  event={todaysEvent}
                  todaysEvent
                  isLoading={isLoading}
                  onCardClick={handleEventClick}
                />
              </>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <MobileSectionHeader
                id="leadership-training-events-header-mobile"
                headerName={t(
                  'LeadershipTrainingEvents.Card.Header.Events',
                  'Events'
                )}
                component="h2"
              />
              {filterControls}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {isInitialLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <LoadingProgress />
                </Box>
              )}
              {!isInitialLoading && events.length === 0 && (
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'center',
                    color: theme.palette.textOrIcon.subheader,
                  }}
                >
                  {t(
                    'LeadershipTrainingEvents.Table.NoEvents',
                    'No Events found'
                  )}
                </Typography>
              )}
              {events.map((event) =>
                event ? (
                  <EventCardMobile
                    key={event.eventKey}
                    event={event}
                    isLoading={false}
                    onCardClick={handleEventClick}
                  />
                ) : null
              )}
            </Box>
            {hasMoreEvents && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 2,
                }}
              >
                <OutlinedButton
                  id="leadership-events-view-more"
                  variant={OutlinedButtonVariant.ViewMore}
                  onClick={handleViewMoreClick}
                />
              </Box>
            )}
          </Box>
        ) : (
          <>
            {todaysEvent && (
              <TodaysEventCard
                isLoading={isLoading}
                todaysEvent={todaysEvent}
                headerLabel={todaysEventHeaderLabel}
                onClick={() => handleEventClick(todaysEvent)}
              />
            )}
            <Card>
              <CardFormHeader
                header={
                  <Header
                    id="leadership-training-events-header"
                    headerName={t('Events.Card.Header', 'Events')}
                    component="h2"
                    variant={HeaderVariant.Card}
                  />
                }
                buttons={desktopFilterControls}
              />
              <Box>
                <EventSummaryTable
                  events={events}
                  isLoading={isLoading}
                  pagination={pagination}
                  handlePaginationChange={handlePaginationChange}
                  onEventClick={handleEventClick}
                />
              </Box>
            </Card>
          </>
        )}
      </Box>
    </Page>
  )
}

export default LeadershipTrainingEvents
